/**
 * Tower geometry and motion timing.
 *
 * Pure logic: no Phaser imports. The renderer asks this module where a platform
 * sits and how long a jump should take, so both are unit testable and Prompt 5
 * can reuse the timing curves for multi-platform launches.
 */
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PORTRAIT_DOCK_TOP } from '../types';

/**
 * Vertical gap between platform centres, in logical pixels.
 *
 * CLAUDE.md asks for ~140. At exactly 140 the clear air under the next
 * platform works out at 107px, and the tallest animal is also 107px, so a
 * standing animal's head touched the platform above it the whole time it was
 * waiting for the player to type. `standingHeadroom` states that relationship
 * and a test holds it, so this number cannot drift back without failing.
 */
export const PLATFORM_SPACING = 160;

/** Platform 0 (the starting floe) sits here. */
export const GROUND_Y = 560;

/** The animal's feet rest this far above a platform's centre. */
export const PLATFORM_SURFACE_INSET = 10;

/** Platform art is 56px tall, with the slab's underside 5px above its base. */
export const PLATFORM_ART_HEIGHT = 56;
export const PLATFORM_ART_BOTTOM_PAD = 5;

/**
 * The tallest animal as drawn: the emperor penguin is 134px of art in its
 * 160px box (docs/ART_SPEC.md), rendered at ANIMAL_SCALE = 0.8.
 */
export const TALLEST_ANIMAL_HEIGHT = 108;

/**
 * Clear air between the surface an animal stands on and the underside of the
 * platform above it.
 *
 * This has to stay above TALLEST_ANIMAL_HEIGHT. Below it the animal overlaps
 * the platform above while it stands and waits, which reads as the platforms
 * being too close together even though their spacing is perfectly even.
 */
export function standingHeadroom(): number {
  return (
    PLATFORM_SPACING - PLATFORM_SURFACE_INSET - (PLATFORM_ART_HEIGHT / 2 - PLATFORM_ART_BOTTOM_PAD)
  );
}

/**
 * The word panel floats this far above the platform it belongs to. The panel is
 * about 132px tall and centred on this point, so anything under ~150 overlaps
 * the platform art.
 */
export const WORD_PANEL_OFFSET = 158;

/** Camera headroom above the goal, for the igloo art and the word panel. */
export const TOWER_HEADROOM = 360;

/** Camera room below the starting floe, so it is not flush with the screen edge. */
export const TOWER_FOOTROOM = IS_PORTRAIT
  ? // Tall stage: the start floe sits just above the word dock.
    GAME_HEIGHT - (PORTRAIT_DOCK_TOP - 50)
  : 160;

export const BASE_JUMP_MS = 450;
export const PER_PLATFORM_JUMP_MS = 120;
export const BASE_ARC_HEIGHT = 70;
export const PER_PLATFORM_ARC = 30;

/** Centre y of platform `index`. Taller index means smaller (higher) y. */
export function platformY(index: number): number {
  return GROUND_Y - index * PLATFORM_SPACING;
}

/** Where the animal's feet sit when standing on `index`. */
export function animalY(index: number): number {
  return platformY(index) - PLATFORM_SURFACE_INSET;
}

/** Where the word panel for `index` floats. */
export function wordPanelY(index: number): number {
  return platformY(index) - WORD_PANEL_OFFSET;
}

export interface TowerBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Camera bounds covering the whole tower, so it never scrolls past either end. */
export function towerBounds(platformCount: number): TowerBounds {
  const top = platformY(Math.max(1, platformCount) - 1) - TOWER_HEADROOM;
  const bottom = GROUND_Y + TOWER_FOOTROOM;
  return { x: 0, y: top, width: GAME_WIDTH, height: bottom - top };
}

/** A 1-platform hop takes 450ms; each extra platform adds 120ms. */
export function jumpDurationFor(distance: number): number {
  const d = Math.max(1, Math.round(distance));
  return BASE_JUMP_MS + (d - 1) * PER_PLATFORM_JUMP_MS;
}

/** Longer launches arc higher, so a +6 jetpack flight clears the screen nicely. */
export function arcHeightFor(distance: number): number {
  const d = Math.max(1, Math.round(distance));
  return BASE_ARC_HEIGHT + (d - 1) * PER_PLATFORM_ARC;
}

export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Position along a tweened arc at progress `t` (0..1). A sine bump gives the
 * hop its shape without involving the physics engine (CLAUDE.md).
 */
export function arcPoint(from: Point, to: Point, t: number, arcHeight: number): Point {
  const clamped = Math.min(1, Math.max(0, t));
  return {
    x: lerp(from.x, to.x, clamped),
    y: lerp(from.y, to.y, clamped) - arcHeight * Math.sin(Math.PI * clamped),
  };
}

/** Clamp a landing target to the tower, so a bonus can never overshoot the goal. */
export function clampPlatformIndex(index: number, platformCount: number): number {
  if (!Number.isFinite(index)) return 0;
  return Math.min(platformCount - 1, Math.max(0, Math.round(index)));
}
